import { usePropertyOptional } from "../components/property-provider";
import { useKeycloak } from "../components/keycloak-provider";
import { useMemo } from "react";
import { BACKOFFICE_RESOURCE, decodeRptToken, useUserAuthInfo } from "./auth";

/**
 * Represents an action.
 *
 * Actions are operations that the user can perform either on a property or on the system (i.e. global actions)
 * Actions are authorized by the system, so this representations helps in showing or hiding actions that
 * the user is not allowed to perform.
 *
 * Property actions are validated against authorization scopes on a property RPT
 * Global actions are validated against roles in the user's JWT configured on the backoffice client.
 *
 * Action definitions are auto-generated from witbooking's authorization master. Generated actions
 * can be validated with the useAuthorizer hook.
 */
export interface IAuthzAction {
  /**
   * The unique id of the action.
   */
  id: string;
  /**
   * Whether this action is a property action or a global action
   */
  type: "property" | "global";
  /**
   * The authorization scopes (if the action is a property action) or the user roles (if it's a global action)
   * that allow the user to perform the action.
   */
  enablingScopes: string[];
}

export interface IAuthorizer {
  getRpt(): string | undefined;
  canExecute(action: IAuthzAction): boolean;
}

export function useAuthorizer(): IAuthorizer {
  const keycloak = useKeycloak();
  const userAuthInfo = useUserAuthInfo();
  const property = usePropertyOptional();

  return useMemo(
    () => ({
      getRpt() {
        return property?.token;
      },
      canExecute(action: IAuthzAction): boolean {
        if (action.enablingScopes.includes("*")) return true;

        if (!keycloak.tokenParsed) return false;

        switch (action.type) {
          case "global":
            const userRoles =
              keycloak.tokenParsed?.resource_access?.[BACKOFFICE_RESOURCE]?.roles || [];
            return !!userRoles.find(role => action.enablingScopes.includes(role));
          case "property":
            if (!property?.token) return false;
            const decoded = decodeRptToken(property.token);
            // FIXME: remove this once filters are open to all users
            if (
              (userAuthInfo?.isC2tAdmin ||
                userAuthInfo?.isCampingAdmin ||
                userAuthInfo?.isCampingFrAdmin ||
                userAuthInfo?.isAciAdmin) &&
              action.id.includes("Filter")
            ) {
              return true;
            }
            const userScopes =
              decoded?.authorization.permissions?.find(
                v => v.rsname === `property:${property?.ticker}`
              )?.scopes || [];
            return !!userScopes.find(role => action.enablingScopes.includes(role));
          default:
            throw new Error("Invalid action type.");
        }
      },
    }),
    [
      property?.token,
      property?.ticker,
      keycloak.tokenParsed,
      userAuthInfo?.isC2tAdmin,
      userAuthInfo?.isCampingAdmin,
      userAuthInfo?.isCampingFrAdmin,
      userAuthInfo?.isAciAdmin,
    ]
  );
}
